import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Query } from "appwrite";
import { Eye, Search } from "lucide-react";
import "./AdminOrders.css";
import { useTitle } from "../../hooks/useTitle";
import { tablesDB, DB_ID, ORDERS_TABLE } from "../../appwrite";

const PER_PAGE = 15;

const parseInfoArray = (arr) => {
  if (!Array.isArray(arr)) return {};
  const r = {};
  arr.forEach((item) => {
    if (typeof item === "string") {
      try { Object.assign(r, JSON.parse(item)); }
      catch { const i = item.indexOf(":"); if (i > -1) r[item.slice(0, i).trim()] = item.slice(i + 1).trim(); }
    }
  });
  return r;
};

const fmtDate = (v) => {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleDateString("en-NG", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

const STATUS_MAP = {
  pending: { label: "Pending", cls: "ord-badge--pending" },
  "out-for-delivery": { label: "Out for Delivery", cls: "ord-badge--shipping" },
  delivered: { label: "Delivered", cls: "ord-badge--delivered" },
  cancelled: { label: "Cancelled", cls: "ord-badge--cancelled" },
};

export default function AdminOrders() {
  useTitle("Orders - TrendBay Admin");
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: ORDERS_TABLE,
          queries: [Query.orderDesc("$createdAt"), Query.limit(1000)],
        });
        const rows = (res.rows || res.documents || []).map((r) => ({
          id: r.$id,
          ...r,
        }));
        setOrders(rows);
        setFiltered(rows);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const q = query.toLowerCase();
    setFiltered(
      orders.filter(
        (o) =>
          String(o.orderNumber || "")
            .toLowerCase()
            .includes(q) ||
          (o.fulfillmentStatus || "").toLowerCase().includes(q) ||
          (o.paymentStatus || "").toLowerCase().includes(q),
      ),
    );
    setPage(1);
  }, [query, orders]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const slice = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div className="ord-page">
      <div className="ord-header">
        <div>
          <h2 className="ord-title">Orders</h2>
          <p className="ord-subtitle">
            {filtered.length} order{filtered.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      <div className="ord-search-wrap">
        <Search size={15} className="ord-search-icon" />
        <input
          className="ord-search"
          type="text"
          placeholder="Search by order #, status…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="ord-table-wrap">
        {loading ? (
          <div className="ord-empty">Loading orders…</div>
        ) : filtered.length === 0 ? (
          <div className="ord-empty">No orders found</div>
        ) : (
          <table className="ord-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Order</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Fulfillment</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {slice.map((order, idx) => {
                const info = parseInfoArray(order.shippingInfo);
                const fs = STATUS_MAP[order.fulfillmentStatus] || {
                  label: order.fulfillmentStatus || "-",
                  cls: "ord-badge--pending",
                };
                const paid =
                  (order.paymentStatus || "").toLowerCase() === "paid";
                return (
                  <tr
                    key={order.id}
                    onClick={() => navigate(`/d/order-details/${order.id}`)}
                    className="ord-row"
                  >
                    <td className="ord-td-num">
                      {(page - 1) * PER_PAGE + idx + 1}
                    </td>
                    <td className="ord-order-num">
                      #{order.orderNumber || "-"}
                    </td>
                    <td>{fmtDate(order.$createdAt)}</td>
                    <td>{info.fullName || "-"}</td>
                    <td className="ord-total">
                      ₦
                      {parseFloat(order.orderTotal || 0).toLocaleString(
                        "en-NG",
                        { minimumFractionDigits: 0 },
                      )}
                    </td>
                    <td>
                      <span
                        className={`ord-badge ${paid ? "ord-badge--paid" : "ord-badge--unpaid"}`}
                      >
                        {paid ? "Paid" : order.paymentStatus || "Pending"}
                      </span>
                    </td>
                    <td>
                      <span className={`ord-badge ${fs.cls}`}>{fs.label}</span>
                    </td>
                    <td>
                      <button
                        className="ord-view-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/d/order-details/${order.id}`);
                        }}
                      >
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Mobile cards */}
      <div className="ord-mobile-cards">
        {!loading && slice.map((order) => {
          const info = parseInfoArray(order.shippingInfo);
          const fs = STATUS_MAP[order.fulfillmentStatus] || { label: order.fulfillmentStatus || "-", cls: "ord-badge--pending" };
          const paid = (order.paymentStatus || "").toLowerCase() === "paid";
          return (
            <div
              className="ord-mobile-card"
              key={order.id}
              onClick={() => navigate(`/d/order-details/${order.id}`)}
            >
              <div className="ord-mobile-card__top">
                <span className="ord-order-num">#{order.orderNumber || "-"}</span>
                <span className={`ord-badge ${fs.cls}`}>{fs.label}</span>
              </div>
              <div className="ord-mobile-card__mid">
                <span>{info.fullName || "-"}</span>
                <span className="ord-total">₦{parseFloat(order.orderTotal || 0).toLocaleString("en-NG", { minimumFractionDigits: 0 })}</span>
              </div>
              <div className="ord-mobile-card__bot">
                <span style={{ fontSize: 12, color: "#aaa" }}>{fmtDate(order.$createdAt)}</span>
                <span className={`ord-badge ${paid ? "ord-badge--paid" : "ord-badge--unpaid"}`}>{paid ? "Paid" : order.paymentStatus || "Pending"}</span>
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && <div className="ord-empty">No orders found</div>}
      </div>

      {totalPages > 1 && (
        <div className="ord-pagination">
          <button
            className="ord-page-btn"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            ← Prev
          </button>
          <span className="ord-page-info">
            Page {page} of {totalPages}
          </span>
          <button
            className="ord-page-btn"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
